import {
	type NavbarBranchItem,
	type SharedWithMeListAccess,
	getSharedWithMeListItemAccess,
	navbarBranchIsSharedOrPublished,
} from "@src/components/board/queries/fetch-ranklist";
import type {
	CardEntityDocumentId,
	WhiteboardDocumentId,
} from "@src/components/collaboration/document-id";
import {
	type NavbarDocumentKind,
	tryParseNavbarDocumentId,
} from "@src/components/collaboration/document-id/factories/parse-navbar-document";
import { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import type { TreeItem } from "@src/lib/clean-tree";

type NavTreeItemBase = TreeItem & {
	name: string;
	icon?: string | null;
	/** My Boards (etc.): collaborators or published link — not used on Shared With Me. */
	isSharedOrPublished?: boolean;
	/** Shared With Me boards only: read vs write affordance in the tree. */
	sharedWithMeListItemAccess?: SharedWithMeListAccess;
};

export type NavFolderTreeItem = NavTreeItemBase & {
	kind: "folder";
	isFolder: true;
};

export type NavBoardTreeItem = NavTreeItemBase & {
	kind: "board";
	isFolder: false;
	whiteboardId: WhiteboardDocumentId;
};

export type NavCardTreeItem = NavTreeItemBase & {
	kind: "card";
	isFolder: false;
	cardId: CardEntityDocumentId;
};

export type NavTreeItem =
	| NavFolderTreeItem
	| NavBoardTreeItem
	| NavCardTreeItem;

function toNavbarDocumentKind(type: NavBarItemType): NavbarDocumentKind | null {
	switch (type) {
		case NavBarItemType.Folder:
			return "folder";
		case NavBarItemType.Board:
			return "board";
		case NavBarItemType.Card:
			return "card";
		default:
			return null;
	}
}

/**
 * Maps a navbar API branch row into a discriminated tree leaf.
 * Returns null when the key cannot be parsed for the declared type
 * (soft-fail so ErrorBoundary does not swallow the whole nav section).
 */
export function toNavTreeItem(
	entry: NavbarBranchItem,
	section: NavbarSection,
	currentUserId: string,
): NavTreeItem | null {
	const kind = toNavbarDocumentKind(entry.type);
	if (kind == null) {
		console.error(`[navbar] Skipping item ${entry.key}: unsupported type`);
		return null;
	}

	const parsed = tryParseNavbarDocumentId(entry.key, kind);
	if (!parsed.ok) {
		console.error(`[navbar] Skipping item ${entry.key}: ${parsed.error}`);
		return null;
	}

	const sharedFields = {
		id: entry.key,
		name: entry.name,
		icon: entry.icon?.value ?? null,
		hasChildren: entry.hasChildren,
		...(section === NavbarSection.SharedWithMe &&
		entry.type !== NavBarItemType.Folder
			? {
					sharedWithMeListItemAccess: getSharedWithMeListItemAccess(
						entry,
						currentUserId,
					),
				}
			: {
					isSharedOrPublished: navbarBranchIsSharedOrPublished(entry),
				}),
	};

	if (parsed.kind === "folder") {
		return {
			...sharedFields,
			kind: "folder",
			isFolder: true,
		};
	}

	if (parsed.kind === "board") {
		return {
			...sharedFields,
			kind: "board",
			isFolder: false,
			whiteboardId: parsed.documentId,
		};
	}

	return {
		...sharedFields,
		kind: "card",
		isFolder: false,
		cardId: parsed.documentId,
	};
}
